import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { App, Button, Form, Input, Result, Typography } from 'antd'
import { z } from 'zod'

import { useWardenRegisterMutation } from '@/apis/warden-auth.api'
import { LockOutlined } from '@/icons'

import logo from '../../-assets/logo.svg'
import { useFormValidation } from '../../-hooks/useFormValidation'

const { Title } = Typography

export const Route = createFileRoute('/auth/warden/register')({
  component: WardenRegisterPage,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
})

type RegisterFormValues = {
  password: string
  confirmPassword: string
}

function WardenRegisterPage() {
  const { t } = useLingui()
  const { message } = App.useApp()
  const { token } = Route.useSearch()
  const { getPasswordRules } = useFormValidation()
  const registerMutation = useWardenRegisterMutation()
  const navigate = useNavigate()

  if (!token) {
    return <Result status="error" title={t`Invalid or expired invitation link`} />
  }

  const onFinish = async (values: RegisterFormValues) => {
    if (values.password !== values.confirmPassword) {
      void message.error(t`Passwords do not match!`)
      return
    }
    try {
      await registerMutation.mutateAsync({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      })
      void message.success(t`Registration successful`)
      void navigate({
        to: '/auth/success',
        search: {
          success_type: 'register_success',
        },
      })
    } catch {
      void message.error(t`Registration failed: `)
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto', padding: '32px 0' }}>
      <div className="flex justify-center">
        <img src={logo} alt="" className="mb-8" />
      </div>
      <Title level={1} className="mb-1">
        <Trans>Complete Registration</Trans>
      </Title>
      <p className="mb-6 text-sm text-gray-500">
        <Trans>Set a password to activate your Warden account</Trans>
      </p>

      <Form<RegisterFormValues> onFinish={onFinish} layout="vertical" size="large">
        <Form.Item name="password" rules={getPasswordRules(8)}>
          <Input.Password prefix={<LockOutlined />} placeholder={t`Enter password`} />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: t`Please confirm password!` },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) return Promise.resolve()
                return Promise.reject(new Error(t`Passwords do not match!`))
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder={t`Enter password again`} />
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={registerMutation.isPending}
            block
            size="large"
          >
            <Trans>Complete Registration</Trans>
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
